import { FileSignature, PenSquare, ShieldCheck, Type } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProposalDocumentLayoutPage } from "../../types";

interface BuilderSignaturePageSettingsProps {
  page: ProposalDocumentLayoutPage;
  readOnly?: boolean;
  onChange: (updates: Partial<ProposalDocumentLayoutPage>) => void;
  className?: string;
}

type SignaturePageSettings = Record<string, string | boolean | number | null>;

function getSettings(page: ProposalDocumentLayoutPage): SignaturePageSettings {
  return page.settings && typeof page.settings === "object"
    ? (page.settings as SignaturePageSettings)
    : {};
}

function getStringSetting(
  settings: SignaturePageSettings,
  key: string,
  fallback = ""
) {
  const value = settings[key];
  return typeof value === "string" ? value : fallback;
}

function getBooleanSetting(
  settings: SignaturePageSettings,
  key: string,
  fallback: boolean
) {
  const value = settings[key];
  return typeof value === "boolean" ? value : fallback;
}

function updateSetting(
  settings: SignaturePageSettings,
  key: string,
  value: string | boolean | number | null
) {
  return {
    settings: {
      ...settings,
      [key]: value,
    },
  };
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="border-b border-white/10 px-4 py-3">
        <p className="text-sm font-medium text-white/88">{title}</p>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
        ) : null}
      </div>

      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function FieldLabel({
  label,
  icon,
  hint,
}: {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-2">
      <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-white/35">
        {icon}
        {label}
      </label>
      {hint ? <p className="mt-1 text-xs text-white/35">{hint}</p> : null}
    </div>
  );
}

function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/20 text-white"
      />

      <div>
        <p className="text-sm font-medium text-white/86">{label}</p>
        <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
      </div>
    </label>
  );
}

export function BuilderSignaturePageSettings({
  page,
  readOnly = false,
  onChange,
  className,
}: BuilderSignaturePageSettingsProps) {
  const settings = getSettings(page);

  const title = getStringSetting(settings, "title", page.title ?? "Signature Page");
  const approvalHeading = getStringSetting(
    settings,
    "approvalHeading",
    "Acceptance & Signature"
  );
  const approvalText = getStringSetting(settings, "approvalText");
  const signerLabel = getStringSetting(settings, "signerLabel", "Authorized Signer");
  const requireSignature = getBooleanSetting(settings, "requireSignature", true);
  const requirePrintedName = getBooleanSetting(
    settings,
    "requirePrintedName",
    true
  );
  const requireSignedDate = getBooleanSetting(settings, "requireSignedDate", true);
  const showTermsAcknowledgement = getBooleanSetting(
    settings,
    "showTermsAcknowledgement",
    true
  );

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-white/10 bg-black/20",
        className
      )}
    >
      <div className="border-b border-white/10 bg-gradient-to-r from-cyan-500/10 via-blue-500/6 to-transparent px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-2 text-white/65">
            <FileSignature className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">
              Signature Page Settings
            </p>
            <p className="mt-1 text-xs leading-5 text-white/40">
              Adjust the approval framing, signer language, and required fields
              that shape the final acceptance step on the stage.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <SectionCard
          title="Approval framing"
          description="Set the visible title and approval heading shown above the signer area."
        >
          <div>
            <FieldLabel
              label="Page title"
              icon={<Type className="h-4 w-4" />}
              hint="Visible title shown at the top of the signature page."
            />
            <input
              value={title}
              onChange={(e) =>
                onChange(updateSetting(settings, "title", e.target.value))
              }
              disabled={readOnly}
              placeholder="Signature Page"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div>
            <FieldLabel
              label="Approval heading"
              icon={<ShieldCheck className="h-4 w-4" />}
              hint="Primary heading shown above the acceptance area."
            />
            <input
              value={approvalHeading}
              onChange={(e) =>
                onChange(updateSetting(settings, "approvalHeading", e.target.value))
              }
              disabled={readOnly}
              placeholder="Acceptance & Signature"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Approval copy"
          description="Explain what the signer is accepting before they complete the document."
        >
          <div>
            <FieldLabel
              label="Approval text"
              icon={<PenSquare className="h-4 w-4" />}
              hint="Use this area to explain what the signature confirms."
            />
            <textarea
              rows={5}
              value={approvalText}
              onChange={(e) =>
                onChange(updateSetting(settings, "approvalText", e.target.value))
              }
              disabled={readOnly}
              placeholder="Explain what the signer is approving by signing this proposal."
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div>
            <FieldLabel
              label="Signer label"
              hint="Label shown for the signer identity field or signature line."
            />
            <input
              value={signerLabel}
              onChange={(e) =>
                onChange(updateSetting(settings, "signerLabel", e.target.value))
              }
              disabled={readOnly}
              placeholder="Authorized Signer"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Required signer fields"
          description="Choose which signer inputs and acknowledgement language appear on the approval page."
        >
          <ToggleField
            label="Require signature"
            description="Include a signature field on the approval page."
            checked={requireSignature}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "requireSignature", checked))
            }
          />

          <ToggleField
            label="Require printed name"
            description="Include a printed or full-name field for the signer."
            checked={requirePrintedName}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "requirePrintedName", checked))
            }
          />

          <ToggleField
            label="Require signed date"
            description="Include a signed-date field on the approval page."
            checked={requireSignedDate}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "requireSignedDate", checked))
            }
          />

          <ToggleField
            label="Show terms acknowledgement"
            description="Display acknowledgement language above the signer fields."
            checked={showTermsAcknowledgement}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showTermsAcknowledgement", checked))
            }
          />
        </SectionCard>
      </div>
    </div>
  );
}